import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dumbbell, ArrowLeft, Lock, Loader2, CheckCircle, XCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenEmail, setTokenEmail] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/auth/verify-reset-token/${token}`);
        setTokenValid(res.data.valid);
        setTokenEmail(res.data.email || "");
      } catch {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };
    
    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error("Adgangskode skal være mindst 6 tegn");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("Adgangskoderne matcher ikke");
      return;
    }
    
    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        token, 
        new_password: password 
      });
      setSuccess(true);
      toast.success("Adgangskode nulstillet!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Der opstod en fejl");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjA3ODB8MHwxfHNlYXJjaHwxfHxneW0lMjBkYXJrfGVufDB8fHx8MTcwOTMwMDAwMHww&ixlib=rb-4.0.3&q=85"
            alt="Gym"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/90" />
        </div>
        
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-16">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-lg bg-[#DFFF00] flex items-center justify-center">
              <Dumbbell className="h-6 w-6 text-black" />
            </div>
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-2xl uppercase tracking-wider text-white">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </div>

          <h2 style={{ fontFamily: 'Oswald, sans-serif' }} className="text-4xl xl:text-5xl font-bold uppercase text-white leading-tight mb-6">
            Ny<br />
            <span className="text-[#DFFF00]">adgangskode</span>
          </h2>

          <p className="text-zinc-400 text-lg max-w-md">
            Vælg en ny sikker adgangskode til din konto.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <Link to="/login" className="inline-flex items-center gap-2 mb-8 text-zinc-500 hover:text-white transition-colors text-sm" data-testid="reset-back">
            <ArrowLeft className="h-4 w-4" /> Tilbage til login
          </Link>

          {/* Mobile logo */}
          <div className="flex items-center gap-2 mb-6 lg:hidden">
            <Dumbbell className="h-6 w-6 text-[#DFFF00]" />
            <span style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase tracking-wider text-white">
              Fitrack<span className="text-[#DFFF00]">R</span>
            </span>
          </div>

          {verifying ? (
            <div className="text-center py-12">
              <Loader2 className="h-12 w-12 text-[#DFFF00] animate-spin mx-auto mb-4" />
              <p className="text-zinc-400">Verificerer link...</p>
            </div>
          ) : !token || !tokenValid ? (
            <div className="text-center" data-testid="reset-invalid">
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                <XCircle className="h-10 w-10 text-red-400" />
              </div>
              <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-4">
                Ugyldigt link
              </h1>
              <p className="text-zinc-400 mb-8">
                Dette nulstillingslink er ugyldigt eller udløbet. Anmod om et nyt link.
              </p>
              <Link to="/forgot-password">
                <Button className="bg-[#DFFF00] text-black hover:bg-[#C8E600] rounded-lg font-bold uppercase tracking-wider">
                  Anmod om nyt link
                </Button>
              </Link>
            </div>
          ) : success ? (
            <div className="text-center" data-testid="reset-success">
              <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-400" />
              </div>
              <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-4">
                Adgangskode nulstillet!
              </h1>
              <p className="text-zinc-400 mb-8">
                Din adgangskode er blevet opdateret. Du kan nu logge ind med din nye adgangskode.
              </p>
              <Link to="/login">
                <Button 
                  className="bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_30px_rgba(223,255,0,0.3)] rounded-lg font-bold uppercase tracking-wider h-14 px-8"
                  data-testid="reset-login-btn"
                >
                  Gå til login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8 p-4 bg-gradient-to-r from-[#DFFF00]/10 to-[#DFFF00]/5 border border-[#DFFF00]/20 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-[#DFFF00]/20 flex items-center justify-center flex-shrink-0">
                    <Lock className="h-6 w-6 text-[#DFFF00]" />
                  </div>
                  <div>
                    <p style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-lg uppercase text-[#DFFF00]">
                      Nulstil Adgangskode
                    </p>
                    <p className="text-sm text-zinc-400">
                      For: {tokenEmail}
                    </p>
                  </div>
                </div>
              </div>

              <h1 style={{ fontFamily: 'Oswald, sans-serif' }} className="font-bold text-3xl uppercase tracking-tight text-white mb-2" data-testid="reset-title">
                Ny adgangskode
              </h1>
              <p className="text-zinc-400 mb-8">
                Vælg en ny sikker adgangskode (mindst 6 tegn).
              </p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Ny adgangskode</Label>
                  <Input 
                    id="password" 
                    type="password" 
                    value={password} 
                    onChange={e => setPassword(e.target.value)} 
                    required
                    className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] focus:ring-[#DFFF00]/20 rounded-lg h-12 text-white placeholder:text-zinc-600" 
                    data-testid="reset-password" 
                    placeholder="Mindst 6 tegn" 
                  />
                </div>
                
                <div>
                  <Label htmlFor="confirmPassword" className="text-xs uppercase tracking-wider text-zinc-500 mb-1.5 block">Bekræft adgangskode</Label>
                  <Input 
                    id="confirmPassword" 
                    type="password" 
                    value={confirmPassword} 
                    onChange={e => setConfirmPassword(e.target.value)} 
                    required
                    className="bg-zinc-900/50 border-zinc-800 focus:border-[#DFFF00] focus:ring-[#DFFF00]/20 rounded-lg h-12 text-white placeholder:text-zinc-600" 
                    data-testid="reset-confirm-password" 
                    placeholder="Gentag adgangskode" 
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={loading} 
                  data-testid="reset-submit"
                  className="w-full bg-[#DFFF00] text-black hover:bg-[#C8E600] hover:shadow-[0_0_30px_rgba(223,255,0,0.3)] rounded-lg font-bold uppercase tracking-wider h-14 text-base transition-all duration-300"
                >
                  {loading ? (
                    <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Nulstiller...</>
                  ) : (
                    "Nulstil adgangskode"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
